<script>
    import { onMount } from 'svelte';
    import PopupPlanDetail from '@components/PopupPlanDetail.svelte';
    import PopupPlanReorder from '@components/PopupPlanReorder.svelte';
    import { formatDateToKorean } from '@lib/util/dateUtils';
    import { getPlan } from '@lib/api/plan';

    // 상태 변수
    let isRegisterPopupOpen = false; // 플랜 등록 팝업 열림 여부
    let isModifyPopupOpen = false;   // 플랜 수정 팝업 열림 여부
    let isReorderPopupOpen = false;  // 플랜 순서 변경 팝업 열림 여부   
    let plans = [];                  // API에서 받아온 플랜 목록
    let loading = true;              // 플랜 데이터 로딩 상태
    let error = '';                  // 에러 메시지 (빈 문자열이면 에러 없음)
    let size = 9999;                 // 페이지당 플랜 수 (기본: 9999 전체조회)
    let selectedPlan = null;         // 현재 선택된 플랜 정보

    const defaultSearchParam = {
        page: 1,
        size: size
    };

    let searchParam = { ...defaultSearchParam }; // 초기값 세팅

    /**
     * 플랜 목록을 API로부터 불러와 `plans` 배열에 저장합니다.
     *
     * - `getPlan` API를 호출하여 페이징된 데이터를 받아옵니다.
     * - 각 플랜의 단위(currency, period)를 사람이 읽을 수 있는 텍스트로 변환합니다.
     * - 변환된 텍스트는 `unit` 필드로 추가되어 템플릿에서 사용됩니다.
     * - 로딩 상태와 에러 메시지를 상태 변수로 관리합니다.
     *
     * @async
     * @function fetchPlans
     * @returns {Promise<void>}
     */
    async function fetchPlans() {
        loading = true;
        try {
            const data = await getPlan(searchParam);
            plans = data.items.map(plan => {
                const currencyText = translateCurrency(plan.currency);
                const periodText = translatePeriod(plan.period);
                return {
                    ...plan,
                    unit: `${currencyText}/${periodText}`
                };
            });
            // 첫 번째 플랜 자동 선택
            if (plans.length > 0) {
                selectedPlan = plans[0];
            } else {
                selectedPlan = null;
            }
        } catch (err) {
            console.error('플랜 불러오기 실패:', err);
            error = '플랜 정보를 불러오지 못했습니다.';
        } finally {
            loading = false;
        }
    }
    /**
     * 테이블에서 선택된 플랜을 상태에 반영합니다.
     * @param {object} plan - 선택한 플랜 객체
     */
    function selectPlan(plan) {
        selectedPlan = plan;
    }

    /**
     * 플랜 등록 팝업을 엽니다.
     * 사용자 UI 상에서 "플랜 등록" 버튼 클릭 시 호출됩니다.
     */
    function openRegisterPopup() {
        isRegisterPopupOpen = true;
    }

    /**
     * 플랜 등록 팝업을 닫습니다.
     * 팝업 내 '닫기' 버튼 또는 외부 클릭 등으로 트리거됩니다.
     */
    function closeRegisterPopup() {
        isRegisterPopupOpen = false;
    }

    /**
     * 플랜 수정 팝업을 엽니다.
     * 예: "수정" 버튼 클릭 시 해당 플랜 데이터를 불러온 후 호출됩니다.
     */
    function openModifyPopup() {
        isModifyPopupOpen = true;
    }

    /**
     * 플랜 수정 팝업을 닫습니다.
     * 팝업 내 '닫기' 버튼 또는 외부 클릭 등으로 트리거됩니다.
     */
    function closeModifyPopup() {
        isModifyPopupOpen = false;
    }  
    /**
     * 노출 순서 변경 팝업을 엽니다.
     * 사용자 UI 상에서 "순서 변경" 버튼 클릭 시 호출됩니다.
     */
    function openReorderPopup() {
    isReorderPopupOpen = true;
    }

    /**
     * 노출 순서 변경 팝업을 닫습니다.
     * 팝업 내 '닫기' 버튼 또는 외부 클릭 등으로 트리거됩니다.
     */
    function closeReorderPopup() {
    isReorderPopupOpen = false;
    }

    /**
     * 플랜 등록 팝업에서 저장 후 닫기 동작을 처리합니다.
     */
    function handleSave() {
        fetchPlans();
        // 저장 로직 추가 필요 (예: POST /plans)
        isRegisterPopupOpen = false;
    }

    /**
     * 플랜 수정 팝업에서 저장 후 닫기 동작을 처리합니다.
     */
    function handleModify() {
        fetchPlans();
        // 수정 완료시 로직 추가 필요 (예: POST /plans)
        isModifyPopupOpen = false;
    }

    /**
     * 노출 순서 변경 팝업에서 저장 후 닫기 동작을 처리합니다.
     * 실제 저장 로직(API 호출 등)은 이후 구현 예정입니다.
     */
    function handleReorderSave() {
    // 저장 로직 추가 필요 (예: PATCH /plans/order)
    isReorderPopupOpen = false;
    }
    // 유틸 함수

    /**
     * 통화 코드(KRW, USD 등)를 사람이 읽을 수 있는 한글 명칭으로 변환합니다.
     *
     * @param {string} code - 변환할 통화 코드 (예: 'KRW', 'USD')
     * @returns {string} - 한글 통화명 (예: '원', '달러'). 알 수 없는 코드일 경우 그대로 반환됩니다.
     */
    function translateCurrency(code) {
        return code === 'KRW' ? '원' : code === 'USD' ? '달러' : code;
    }

    /**
     * 주기 코드(day, month, year 등)를 사람이 읽을 수 있는 한글 명칭으로 변환합니다.
     *
     * @param {string} code - 변환할 주기 코드 (예: 'day', 'month', 'year')
     * @returns {string} - 한글 주기명 (예: '일', '월', '년'). 알 수 없는 코드는 그대로 반환됩니다.
     */
    function translatePeriod(code) {
        return code === 'day' ? '일' : code === 'month' ? '월' : code === 'year' ? '년' : code;
    }

    // 라이프사이클: 컴포넌트 마운트 시 호출
    onMount(() => {
        console.log('onMount 진입');
        fetchPlans();
    });

</script>
<div class="planMng-wrap d-flex flex-column h-100">
    <p class="font-20 font-weight-700">플랜 관리</p>
    <section class="d-flex gap-10 h-100 mt-10">
        <article class="planMng-list flex-1 h-100">
            <div class="d-flex align-center">
                <p class="font-16 font-weight-700">플랜목록</p>
                <button type="button" class="btn bg-blue ml-auto" data-target="popup-plan-order" on:click={openReorderPopup}>노출 순서 변경</button>
                <button type="button" class="btn bg-blue ml-2"  data-target="popup-plan-apply" on:click={openRegisterPopup}>플랜 등록</button>
            </div>
            <div class="d-flex flex-column h-100 mt-2 bg-white box-border">
                <div class="table-wrap">
                    <table class="table-basic table-basic-planMng">
                        <caption></caption>
                        <colgroup>
                            <col style="width:12%">
                            <col style="width:auto">
                            <col style="width:20%">
                            <col style="width:12%">
                            <col style="width:20%">
                            <col style="width:20%">
                        </colgroup>
                        <thead>
                        <tr>
                            <th scope="col">노출순서</th>
                            <th scope="col">플랜명</th>
                            <th scope="col">금액</th>
                            <th scope="col">단위</th>
                            <th scope="col">메모</th>
                            <th scope="col">수정일시</th>
                        </tr>
                        </thead>
                        <tbody>
                        {#if loading}
                            <tr>
                            <td colspan="6" class="text-center">로딩 중...</td>
                            </tr>
                        {:else if error}
                            <tr>
                            <td colspan="6" class="text-center text-red">{error}</td>
                            </tr>
                        {:else if plans.length === 0}
                            <tr>
                            <td colspan="6" class="text-center">등록된 플랜이 없습니다.</td>
                            </tr>
                        {:else}
                            {#each plans as plan, index}
                            <tr on:click={() => selectPlan(plan)} class:selected={selectedPlan?.plan_id === plan.plan_id}>
                                <td>{plan.order}</td>
                                <td>{plan.plan_name}</td>
                                <td>{plan.amount}</td>
                                <td>{plan.unit || '원/월'}</td>
                                <td>{plan.memo}</td>
                                <td>{formatDateToKorean(plan.updated_at)}</td>
                            </tr>
                            {/each}
                        {/if}
                        </tbody>
                    </table>
                </div>
            </div>
        </article>
        <article class="plan-info flex-1 h-100">
            <div class="d-flex align-center">
                <p class="font-16 font-weight-700">플랜 상세 정보</p>
                <div class="btn-group ml-auto">
                    <button type="button" class="btn border-blue btn-edit"  on:click={openModifyPopup}>플랜 수정</button>
                    <button type="button" class="btn bg-blue btn-cancel" style="display: none;">저장</button>
                </div>
            </div>
            <div class="table-wrap mt-2">
                <table class="table-basic table-basic-planInfo">
                    <caption></caption>
                    <colgroup>
                        <col style="width:15%">
                        <col style="width:auto">
                        <col style="width:15%">
                        <col style="width:35%">
                    </colgroup>
                    <tbody>
                    <tr>
                        <th scope="row">플랜명</th>
                        <td>{selectedPlan?.plan_name || '-'}</td>
                        <th scope="row">금액</th>
                        <td>{selectedPlan ? `${selectedPlan.unit}` : '-'}</td>
                    </tr>
                    <tr>
                        <th scope="row">메모</th>
                        <td colspan="3">{selectedPlan?.memo || '-'}</td>
                    </tr>
                    <tr>
                        <th scope="row">수정일시</th>
                        <td>{selectedPlan ? formatDateToKorean(selectedPlan.updated_at) : '-'}</td>
                        <th scope="row">수정자</th>
                        <td>{selectedPlan?.updated_name || '-'}</td>
                    </tr>

                    </tbody>
                </table>
            </div>
            <div class="d-flex flex-column mt-2 bg-white box-border" style="height:calc(100% - 15.4rem);">
                <div class="table-wrap">
                    <table class="table-basic table-basic-planExplain">
                        <caption></caption>
                        <colgroup>
                            <col style="width:auto">
                        </colgroup>
                        <thead>
                            <tr>
                                <th scope="row">플랜 설명</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each selectedPlan?.descriptions ?? [] as desc}
                            <tr>
                                <td>{desc.text}</td>
                            </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            </div>
        </article>
    </section>
</div>
{#if isRegisterPopupOpen}
  <PopupPlanDetail mode="create" onClose={closeRegisterPopup} onSave={handleSave} />
{/if}

{#if isModifyPopupOpen}
  <PopupPlanDetail mode="modify" initialData={selectedPlan} onClose={closeModifyPopup} onSave={handleModify} />
{/if}

{#if isReorderPopupOpen}
  <PopupPlanReorder plans={plans} onClose={closeReorderPopup} onSave={handleReorderSave} />
{/if}